import { Injectable, inject } from '@angular/core';
import {
  IActividadUsuario,
  IEstadisticasPorCliente,
  IEstadisticasPorUsuario,
  IEstadisticasResumen,
  IEstadisticasSeriePunto,
  IListado,
  IQueryParam,
} from 'modelos/src';
import { HttpClientService } from '../http.service';
import { HelperService } from '../helper.service';

export interface IEstadisticasFiltro {
  desde?: string;
  hasta?: string;
  idCliente?: string;
  busqueda?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

/**
 * Estadísticas de uso de la app (actividad de usuarios). Todos los endpoints
 * están restringidos al cliente raíz (nivel 0) en el backend.
 */
@Injectable({
  providedIn: 'root',
})
export class EstadisticasUsoService {
  private http = inject(HttpClientService);

  private params(f: IEstadisticasFiltro): Record<string, string> {
    const p: Record<string, string> = {};
    if (f.desde) p['desde'] = f.desde;
    if (f.hasta) p['hasta'] = f.hasta;
    if (f.idCliente) p['idCliente'] = f.idCliente;
    if (f.busqueda) p['busqueda'] = f.busqueda;
    if (f.page != null) p['page'] = String(f.page);
    if (f.limit != null) p['limit'] = String(f.limit);
    if (f.sort) p['sort'] = f.sort;
    return p;
  }

  public resumen(f: IEstadisticasFiltro): Promise<IEstadisticasResumen> {
    return this.http.get(`/estadisticas-uso/resumen`, {
      params: this.params(f),
    });
  }

  public serie(f: IEstadisticasFiltro): Promise<IEstadisticasSeriePunto[]> {
    return this.http.get(`/estadisticas-uso/serie`, { params: this.params(f) });
  }

  public porCliente(
    f: IEstadisticasFiltro,
  ): Promise<IEstadisticasPorCliente[]> {
    return this.http.get(`/estadisticas-uso/clientes`, {
      params: this.params(f),
    });
  }

  public porUsuario(f: IEstadisticasFiltro): Promise<{
    datos: IEstadisticasPorUsuario[];
    totalCount: number;
  }> {
    return this.http.get(`/estadisticas-uso/usuarios`, {
      params: this.params(f),
    });
  }

  /** Listado de intervalos de inactividad (scoping por permiso en el
   *  backend, requiere acción "Administración - Ver inactividad usuarios"). */
  public getInactividad(
    query: IQueryParam,
  ): Promise<IListado<IActividadUsuario>> {
    const params = HelperService.getQueryParams(query);
    return this.http.get(`/estadisticas-uso/inactividad`, { params });
  }

  // ---- Inactividad del usuario (los manda el propio usuario logueado) ----

  /** El usuario no confirmó el cartel de actividad: abre su intervalo de
   *  inactividad (`inicio` = ahora). El backend saca todo del token/permiso,
   *  no hay body. */
  public registrarInactividad(): Promise<{ ok: boolean }> {
    return this.http.post(`/estadisticas-uso/inactividad`, {});
  }

  /** El usuario volvió a confirmar actividad: cierra su último intervalo. */
  public finalizarInactividad(): Promise<{ ok: boolean }> {
    return this.http.post(`/estadisticas-uso/inactividad/finalizar`, {});
  }

  // ---- Periodo de actividad (los manda el propio usuario logueado) ----

  /** Listado de registros de periodo de actividad (scoping por permiso en el
   *  backend, requiere acción "Administración - Ver periodo actividad
   *  usuarios"). */
  public getPeriodoActividad(
    query: IQueryParam,
  ): Promise<IListado<IActividadUsuario>> {
    const params = HelperService.getQueryParams(query);
    return this.http.get(`/estadisticas-uso/periodo-actividad`, { params });
  }

  /** El uso continuo superó el límite: registra el período de actividad.
   *  `inicio` es el comienzo del ciclo de uso; el resto lo saca el backend del
   *  token/permiso. */
  public registrarPeriodoActividad(inicio: string): Promise<{ ok: boolean }> {
    return this.http.post(`/estadisticas-uso/periodo-actividad`, { inicio });
  }
}
